import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, MenuItem, Select, InputAdornment
} from "@mui/material";
import API_BASE_URL from "../../../../Constants";

const itemGroupOptions = [
    "Bid Items",
    "Maintenance Contract Items",
    "Other Items",
    "Proposal Items",
    "Tests / Inspections / Violations",
    "User Items"
];

const ManageWorkItemsModal = ({ open, handleClose, phaseIndex, phaseId, allWorkItems, setAllWorkItems, viewOnly }) => {
    // const [assignedWorkItems, setAssignedWorkItems] = useState([]);
    const [selectedWorkItem, setSelectedWorkItem] = useState(null);
    const [editableWorkItems, setEditableWorkItems] = useState([]);
    const [customWorkItems, setCustomWorkItems] = useState([]);  // Locally created work items
    const [isCreatingNew, setIsCreatingNew] = useState(false);


    const [newWorkItem, setNewWorkItem] = useState({
        itemGroup: "Other Items",
        itemType: "",
        itemDescription: "",
        quantity: 1,
        costPerQuantity: 0,
        material: 0,
        rate: 0,
        hours: 0
    });

    useEffect(() => {
        if (open) {
            fetchAssignedWorkItems();
        }
    }, [open]);

    const workItemDescription = (item, allItems = allWorkItems) => {
        const workItem = allItems.find((i) => i.workItemId === item.workItemId);
        return workItem ? workItem.itemDescription : "";

    }

    const fetchAssignedWorkItems = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/workitems-phases/phase/${phaseId}`);
            if (!response.ok) throw new Error("Failed to fetch assigned work items");
            const data = await response.json();

            setEditableWorkItems(data);
        } catch (error) {
            console.error("Error fetching assigned work items:", error);
        }
    };

    // Select an existing work item
    const handleSelectWorkItem = (workItemId) => {
        const workItem = [...allWorkItems, ...customWorkItems].find((item) => item.workItemId === workItemId);
        if (workItem) {
            setSelectedWorkItem({
                ...workItem,
                quantity: workItem.quantity || 1,
                costPerQuantity: workItem.costPerQuantity || 50,
                material: workItem.material || 100,
                rate: workItem.rate || 20,
                hours: workItem.hours || 8
            });
        }
    };

    // Add selected work item to the list
    const handleAddWorkItem = () => {
        if (selectedWorkItem) {
            if (editableWorkItems.some((item) => item.workItemId === selectedWorkItem.workItemId)) return;
            setEditableWorkItems([...editableWorkItems, selectedWorkItem]);
            setSelectedWorkItem(null);
        }
    };

    // Handle new work item field changes
    const handleNewWorkItemChange = (e) => {
        setNewWorkItem({ ...newWorkItem, [e.target.name]: e.target.value });
    };

    // Create new work item locally
    const handleCreateNewWorkItem = async () => {
        if (!newWorkItem.itemType || !newWorkItem.itemDescription) {
            alert("Item Type and Description are required!");
            return;
        }

        const newItem = { ...newWorkItem };
        try {
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/work-items`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newItem),
            });

            if (!response.ok) throw new Error("Failed to create new work item");
            const data = await response.json();
            setAllWorkItems([...allWorkItems, data]);
            setEditableWorkItems([...editableWorkItems, data]);
            setNewWorkItem({
                itemGroup: "Other Items",
                itemType: "",
                itemDescription: "",
                quantity: 1,
                costPerQuantity: 0,
                material: 0,
                rate: 0,
                hours: 0
            });
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setIsCreatingNew(false);
        }
    };

    // Handle input changes for editable fields
    const handleInputChange = (index, field, value) => {
        const updatedItems = [...editableWorkItems];
        updatedItems[index][field] = value >= 0 ? value : 0;
        setEditableWorkItems(updatedItems);
    };

    // Save selected work items
    const handleSave = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/proposals-and-jobs/workitems-phases/batch`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(editableWorkItems.map(item => ({ ...item, phaseId }))),
            });

            if (!response.ok) throw new Error("Failed to save work items");
            fetchAssignedWorkItems();
            handleClose();
        } catch (error) {
            console.error("Error saving work items:", error);
        }
    };

    // Remove work item from the list
    const handleRemoveWorkItem = (index) => {
        const updatedItems = [...editableWorkItems];
        updatedItems.splice(index, 1);
        setEditableWorkItems(updatedItems);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{viewOnly ? "View" : "Manage"} Work Items for Phase #{phaseIndex}</DialogTitle>
            <DialogContent>



                {/* Create New Work Item Form */}
                {!viewOnly && isCreatingNew && (
                    <div className="bg-gray-100 p-4 rounded-lg my-4">
                        <label>Item Group:</label>
                        <Select name="itemGroup" value={newWorkItem.itemGroup} onChange={handleNewWorkItemChange} fullWidth>
                            {itemGroupOptions.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>

                        <TextField

                            disabled={viewOnly} name="itemType" label="Item Type" fullWidth margin="dense"
                            value={newWorkItem.itemType} onChange={handleNewWorkItemChange} />
                        <TextField

                            disabled={viewOnly} name="itemDescription" label="Description" fullWidth margin="dense"
                            value={newWorkItem.itemDescription} onChange={handleNewWorkItemChange} />
                        <TextField

                            disabled={viewOnly} name="quantity" label="Quantity" type="number" fullWidth margin="dense"
                            value={newWorkItem.quantity} onChange={handleNewWorkItemChange} />

                        <TextField

                            disabled={viewOnly}
                            name="costPerQuantity"
                            label="Cost Per Quantity"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={newWorkItem.costPerQuantity}
                            onChange={handleNewWorkItemChange}
                            onBlur={(e) => {
                                let value = e.target.value.trim();
                                if (value === "" || parseFloat(value) === 0) {
                                    setNewWorkItem({ ...newWorkItem, costPerQuantity: "" }); // Keep empty when 0
                                } else {
                                    setNewWorkItem({ ...newWorkItem, costPerQuantity: parseFloat(value).toFixed(2) });
                                }
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,


                            }}
                        />

                        <TextField

                            disabled={viewOnly}
                            name="material"
                            label="Material"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={newWorkItem.material}
                            onChange={handleNewWorkItemChange}
                            onBlur={(e) => {
                                let value = e.target.value.trim();
                                if (value === "" || parseFloat(value) === 0) {
                                    setNewWorkItem({ ...newWorkItem, material: "" });
                                } else {
                                    setNewWorkItem({ ...newWorkItem, material: parseFloat(value).toFixed(2) });
                                }
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,


                            }}
                        />

                        <TextField

                            disabled={viewOnly}
                            name="rate"
                            label="Rate"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={newWorkItem.rate}
                            onChange={handleNewWorkItemChange}
                            onBlur={(e) => {
                                let value = e.target.value.trim();
                                if (value === "" || parseFloat(value) === 0) {
                                    setNewWorkItem({ ...newWorkItem, rate: "" });
                                } else {
                                    setNewWorkItem({ ...newWorkItem, rate: parseFloat(value).toFixed(2) });
                                }
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,


                            }}
                        />

                        <TextField

                            disabled={viewOnly}
                            name="hours"
                            label="Hours"
                            type="number"
                            fullWidth
                            margin="dense"
                            value={newWorkItem.hours}
                            onChange={handleNewWorkItemChange}
                            onBlur={(e) => {
                                let value = e.target.value.trim();
                                if (value === "" || parseFloat(value) === 0) {
                                    setNewWorkItem({ ...newWorkItem, hours: "" });
                                } else {
                                    setNewWorkItem({ ...newWorkItem, hours: parseFloat(value).toFixed(2) });
                                }
                            }}
                        />


                        <Button variant="contained" color="success" onClick={handleCreateNewWorkItem}>
                            Save Work Item
                        </Button>
                    </div>
                )}
                {/* Select Work Item */}
                {!viewOnly && <div className="flex items-center gap-4 mb-4">
                    <Select
                        value={selectedWorkItem?.workItemId || ""}
                        onChange={(e) => handleSelectWorkItem(e.target.value)}
                        displayEmpty
                        fullWidth
                    >
                        <MenuItem value="" disabled>Select a Work Item</MenuItem>
                        {allWorkItems.map((item) => (
                            <MenuItem key={item.workItemId} value={item.workItemId}>
                                {item.itemGroup} - {item.itemType} - {item.itemDescription}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddWorkItem}
                        disabled={!selectedWorkItem}
                    >
                        Add
                    </Button>
                    {/* Toggle New Work Item Form */}
                    <Button variant="contained" onClick={() => setIsCreatingNew(!isCreatingNew)}>
                        {isCreatingNew ? "Cancel" : "Create"}
                    </Button>
                </div>
                }

                {/* Work Items Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Item Description</TableCell>
                                <TableCell>Quantity</TableCell>
                                <TableCell>Cost per Unit ($)</TableCell>
                                <TableCell>Material ($)</TableCell>
                                <TableCell>Rate ($)</TableCell>
                                <TableCell>Hours</TableCell>
                                {!viewOnly && <TableCell>Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {editableWorkItems.map((workItem, index) => (
                                <TableRow key={workItem.workItemId}>
                                    <TableCell>
                                        {workItemDescription(workItem)}
                                    </TableCell>
                                    <TableCell>
                                        <TextField

                                            disabled={viewOnly}
                                            type="number"
                                            value={workItem.quantity || ""}
                                            onChange={(e) => handleInputChange(index, "quantity", parseInt(e.target.value) || 0)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <TextField

                                            disabled={viewOnly}
                                            type="number"
                                            value={workItem.costPerQuantity || ""}
                                            onChange={(e) => handleInputChange(index, "costPerQuantity", e.target.value)}
                                            onBlur={(e) => {
                                                let value = e.target.value.trim();
                                                handleInputChange(index, "costPerQuantity", value === "" || parseFloat(value) === 0 ? "" : parseFloat(value).toFixed(2));
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,


                                            }}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <TextField

                                            disabled={viewOnly}
                                            type="number"
                                            value={workItem.material || ""}
                                            onChange={(e) => handleInputChange(index, "material", e.target.value)}
                                            onBlur={(e) => {
                                                let value = e.target.value.trim();
                                                handleInputChange(index, "material", value === "" || parseFloat(value) === 0 ? "" : parseFloat(value).toFixed(2));
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,


                                            }}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <TextField

                                            disabled={viewOnly}
                                            type="number"
                                            value={workItem.rate || ""}
                                            onChange={(e) => handleInputChange(index, "rate", e.target.value)}
                                            onBlur={(e) => {
                                                let value = e.target.value.trim();
                                                handleInputChange(index, "rate", value === "" || parseFloat(value) === 0 ? "" : parseFloat(value).toFixed(2));
                                            }}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">$</InputAdornment>,


                                            }}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <TextField

                                            disabled={viewOnly}
                                            type="number"
                                            value={workItem.hours || ""}
                                            onChange={(e) => handleInputChange(index, "hours", e.target.value)}
                                            onBlur={(e) => {
                                                let value = e.target.value.trim();
                                                handleInputChange(index, "hours", value === "" || parseFloat(value) === 0 ? "" : parseFloat(value).toFixed(2));
                                            }}


                                        />
                                    </TableCell>

                                    {!viewOnly && <TableCell>
                                        <Button onClick={() => handleRemoveWorkItem(index)} color="error">
                                            Remove
                                        </Button>
                                    </TableCell>}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions>

                <Button onClick={handleClose} color="primary">
                    {viewOnly ? "Close" : "Cancel"}
                </Button>
                {!viewOnly && <>
                    <Button onClick={handleSave} color="success" variant="contained">
                        Save
                    </Button>
                </>}
            </DialogActions>
        </Dialog>
    );
};

export default ManageWorkItemsModal;
